import { useCallback, useEffect, useMemo, useState } from 'react'
import { unstable_useBlocker } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

export const useNavConfirmModal = () => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const [navigationBlocked, setNavigationBlocked] = useState(false)

  // Block navigation when active is true
  const blocker = unstable_useBlocker(({ currentLocation, nextLocation }) => {
    // Block if active is true and we're actually navigating to a different location
    return navigationBlocked && currentLocation.pathname !== nextLocation.pathname
  })

  // Show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setConfirmModalOpen(true)
    }
  }, [blocker.state])

  // Memoize callback functions to prevent unnecessary re-renders
  const onConfirm = useCallback(() => {
    setConfirmModalOpen(false)
    // Proceed with the blocked navigation
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }, [blocker])

  // Unlocks the navigation when the blocker value changes
  // useEffect(() => {
  //   if (blocker.state === 'blocked') {
  //     blocker.reset()
  //   }
  // }, [blocker])

  const onCancel = useCallback(() => {
    setConfirmModalOpen(false)
    // Reset the blocker to cancel navigation
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  // Memoize the component to prevent unnecessary re-creation
  const NavConfirmModalWrapper = useMemo(() => {
    const Component = () => (
      <NavConfirmModal
        open={confirmModalOpen}
        onClose={onCancel}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    )
    Component.displayName = 'NavConfirmModalWrapper'
    return Component
  }, [confirmModalOpen, onCancel, onConfirm])
  return {
    NavConfirmModal: NavConfirmModalWrapper,
    setNavigationBlocked,
  }
}

export type NavConfirmModalProps = {
  open: boolean
  onClose?: () => void
  onConfirm?: () => void
  onCancel?: () => void
}

const NavConfirmModal = ({ open, onClose, onConfirm, onCancel }: NavConfirmModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      header={{
        type: 'critical',
        title: i18n.t('ai_reports.nav_confirm_modal.title'),
        description: i18n.t('ai_reports.nav_confirm_modal.description'),
      }}
      actions={{
        primary: {
          label: i18n.t('ai_reports.nav_confirm_modal.confirm'),
          onClick: onConfirm,
        },
        secondary: {
          label: i18n.t('ai_reports.nav_confirm_modal.cancel'),
          onClick: onCancel,
        },
      }}
    />
  )
}
